import React, { useEffect } from "react";
import { Select } from "antd";
import { BorderOutlined, TableOutlined, MenuOutlined } from "@ant-design/icons";
import { setWidgetInSheetParams } from "appRedux/actions/Actions";
import { useDispatch, useSelector } from "react-redux";
import IntlMessages from "@util/IntlMessages";
import { useIntl } from "react-intl";
import { showName, validateTranslations } from "@util/Scripts";
import { useWidgetSettings } from "@util/customHooks";

import { Input, Message, Switch, Button, Radio, RadioGroup } from "../../../BP2Components";
import "../GlobalSettings/GlobalSettings.less";
import "./advertisementListSettings.less";

const AdvertisementListSettings = ({ columns, rows, data, value, handleChangeDisplayOption }) => {
   const { allLanguages, locale } = useSelector(({ settings }) => settings);
   const serviceTranslations = useSelector(({ translations }) => translations.translations);
   const { settings, setSettings, handleChange, syncTranslations } = useWidgetSettings(value, {
      sync: [{ keyField: "title", category: "advertisementList.name", valuePrefix: "name" }],
      translations: [{ translationKey: value.title }],
   });

   const dispatch = useDispatch();
   const intl = useIntl();

   useEffect(() => {
      setSettings((prev) => ({
         ...prev,
         ...allLanguages.reduce((acc, { language }) => {
            const serviceVal = language === locale ? serviceTranslations?.[value.title] : null;
            acc[`name${language}`] = serviceVal || showName(language, value.title, data);
            return acc;
         }, {}),
      }));
   }, []);

   const handleSubmit = (e) => {
      e.preventDefault();

      const validTranslations = validateTranslations(settings);

      if (!validTranslations) {
         Message.error(intl.formatMessage({ id: "widget.invalidTranslations" }));
         return;
      }
      dispatch(setWidgetInSheetParams(settings));
      syncTranslations(settings);
      Message.success(intl.formatMessage({ id: "global.saved" }));
   };

   return (
      <form onSubmit={handleSubmit}>
         <div className="widget-settings">
            <div className="widget-settings__inner">
               <label key="widget-title" className="widget-settings__inner__label">
                  <IntlMessages id="settings.title" />:
                  <Input
                     onChange={(e) => handleChange(e.target.value, "title")}
                     value={settings.title}
                     name="title"
                     className="switch_with_label-input"
                  />
               </label>
               {allLanguages?.map(({ language }) => (
                  <label key={language} className="widget-settings__inner__label">
                     <IntlMessages key={`widgetTitle${language}`} id={`widgetTitle${language}`} />:
                     <Input
                        key={language}
                        type="translation"
                        className="translationName"
                        name={`name${language}`}
                        defaultValue={
                           settings?.[`name${language}`]
                              ? settings?.[`name${language}`]
                              : showName(`${language}`, value.title, data)
                        }
                        onChange={(e) => handleChange(e.target.value, e.target.name)}
                        style={{ width: "300px" }}
                     />
                  </label>
               ))}

               <label
                  key="photoColumn"
                  style={{ display: "none" }}
                  className="widget-settings__inner__label"
               >
                  <IntlMessages id="settings.photoColumn" />:
                  <Select
                     defaultValue={settings.photoColumn}
                     // TODO: change this to proper function after component refactor
                     onChange={(value) => handleChange(value, "photoColumn")}
                  >
                     <Option value={"-"}>-</Option>
                     {columns.map(({ title }, index) => (
                        <Option value={title} key={title + index}>
                           {title}
                        </Option>
                     ))}
                  </Select>
               </label>
               <label key="photoColumnTechname" className="widget-settings__inner__label">
                  <IntlMessages id="settings.photoColumn" />:
                  <Select
                     defaultValue={settings.photoColumn}
                     onChange={(value) => handleChange(value, "photoColumn")}
                  >
                     <Option value={"-"}>-</Option>
                     {columns.map(({ title, techName }) => (
                        <Option value={techName} key={title + techName}>
                           {title}
                        </Option>
                     ))}
                  </Select>
               </label>
               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.previewOnClick" />:
                  <Switch
                     onChange={(value) => handleChange(value, "previewOnClick")}
                     checked={settings.previewOnClick}
                  />
               </label>
               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.isPhotoVisible" />:
                  <Switch
                     onChange={(value) => handleChange(value, "isPhotoVisible")}
                     checked={settings.isPhotoVisible}
                  />
               </label>
               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.showDisplayOptions" />:
                  <Switch
                     onChange={(value) => handleChange(value, "showDisplayOptions")}
                     checked={settings.showDisplayOptions}
                  />
               </label>
               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.displayOption" />:
                  <div className="display-options-container-settings">
                     <RadioGroup
                        optionType="button"
                        defaultValue={settings.displayOption || "displayNormal"}
                        onChange={(e) => {
                           handleChange(e.target.value, "displayOption");
                           handleChangeDisplayOption(e.target.value);
                        }}
                        disabled={settings.showDisplayOptions}
                     >
                        <Radio value={"displayNormal"} className="display-normal">
                           <BorderOutlined />
                        </Radio>
                        <Radio value={"displayGrid"} className="display-grid">
                           <TableOutlined />
                        </Radio>
                        <Radio value={"displaySmall"} className="display-small">
                           <MenuOutlined />
                        </Radio>
                     </RadioGroup>
                  </div>
               </label>
               <label key="compactImageSize" className="widget-settings__inner__label">
                  {/*TODO: change those clases to proper names after refactoring whole advertisment widget*/}
                  <IntlMessages id="advertismentListSettings.compactImageSize" /> :
                  <Input
                     addonAfter="px"
                     type="number"
                     min={0}
                     max={250}
                     placeholder="50"
                     name="compactImageSize"
                     onChange={(e) => handleChange(e.target.value, "compactImageSize")}
                     value={settings.compactImageSize}
                     className="switch_with_label-input"
                  />
               </label>

               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.buttonsInSelectMode" />:
                  <Switch
                     onChange={(value) => handleChange(value, "buttonsInSelectMode")}
                     checked={settings.buttonsInSelectMode}
                  />
               </label>
               <label className="widget-settings__inner__label">
                  <IntlMessages id="settings.displayLeft" />:
                  <Switch
                     onChange={(value) => handleChange(value, "displayLeft")}
                     checked={settings.displayLeft}
                  />
               </label>
            </div>
            <Button buttonType="primary" htmlType="submit">
               <IntlMessages id="global.preview" />
            </Button>
         </div>
      </form>
   );
};

export default AdvertisementListSettings;
